import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import PlayModal from "@/components/feature/PlayModal";
import SectionHeading from "@/components/feature/SectionHeading";
import { movies } from "@/mocks/movies";
import type { Movie } from "@/types/movie";
import { formatViews, getMovieById, getRelatedMovies } from "@/utils/movieFilters";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const [playOpen, setPlayOpen] = useState(false);

  const allMovies = movies as Movie[];
  const movie = useMemo(() => getMovieById(allMovies, id ?? ""), [allMovies, id]);
  const related = useMemo(
    () => (movie ? getRelatedMovies(allMovies, movie, 6) : []),
    [allMovies, movie]
  );

  usePageMeta({
    title: movie
      ? `${movie.title} - 高清在线看详情 - 91在线看`
      : "影视详情 - 91在线看",
    description: movie
      ? movie.description.slice(0, 120)
      : "91在线看影视详情页，查看影片资料与相关推荐。",
    keywords: movie
      ? `${movie.title},${movie.title}在线观看,${movie.genres.join(",")},91在线看`
      : "91在线看",
  });

  if (!movie) {
    return (
      <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8 lg:px-12 py-24 flex flex-col items-center text-center">
        <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center text-foreground-500">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-xl md:text-2xl font-bold text-foreground-900">
          未找到该影视资源
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          您访问的影视可能已下架或链接有误，请返回首页重新浏览。
        </p>
        <Link
          to="/"
          className="mt-6 h-10 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-label flex items-center whitespace-nowrap transition-colors cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const fullStars = Math.round(movie.rating / 2);

  return (
    <div className="w-full pb-16 md:pb-24">
      {/* 主图区 */}
      <section className="relative">
        <div className="absolute inset-x-0 top-0 h-[300px] md:h-[380px] overflow-hidden">
          <img
            src={movie.backdrop}
            alt={`${movie.title} 剧照`}
            title={`${movie.title} 高清在线看`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-background-50/40"></div>
        </div>

        <div className="relative mx-auto w-full max-w-[1440px] px-4 md:px-8 lg:px-12 pt-6 md:pt-8">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: movie.type === "movie" ? "电影" : "电视剧", to: `/category/${movie.type}` },
              { label: movie.title },
            ]}
          />

          <div className="mt-6 md:mt-10 flex flex-col md:flex-row gap-6 md:gap-10">
            <div className="w-40 md:w-56 shrink-0 self-center md:self-start rounded-lg overflow-hidden border border-background-200/70 bg-background-200">
              <div className="w-full aspect-[2/3]">
                <img
                  src={movie.cover}
                  alt={`${movie.title} 海报`}
                  title={`${movie.title} 在线观看`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-3 py-1 rounded-full text-xs font-label bg-accent-500 text-background-50">
                  {movie.type === "movie" ? "电影" : "电视剧"}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-label bg-background-200 text-foreground-700">
                  {movie.year}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-label bg-secondary-900/60 text-secondary-200">
                  {movie.region}
                </span>
              </div>

              <h1 className="mt-4 font-heading text-3xl md:text-5xl font-extrabold text-foreground-950 leading-tight">
                {movie.title}
              </h1>

              <div className="mt-4 flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-1">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <i
                      key={i}
                      className={`text-lg ${
                        i < fullStars ? "ri-star-fill text-accent-500" : "ri-star-line text-foreground-400"
                      }`}
                    ></i>
                  ))}
                  <span className="ml-2 font-heading text-xl font-bold text-accent-400">
                    {movie.rating.toFixed(1)}
                  </span>
                </div>
                <span className="text-sm text-foreground-500">
                  <i className="ri-fire-line mr-1 text-accent-400"></i>
                  {formatViews(movie.views)} 次演示播放
                </span>
              </div>

              <div className="mt-4 flex items-center gap-2 flex-wrap">
                {movie.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/category/${movie.type}?genre=${encodeURIComponent(g)}`}
                    className="px-3 py-1 rounded-md text-xs md:text-sm bg-secondary-100/80 text-secondary-900 hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    {g}
                  </Link>
                ))}
              </div>

              <div className="mt-5 space-y-2 text-sm text-foreground-600">
                <p>
                  <span className="text-foreground-500">导演：</span>
                  {movie.director}
                </p>
                <p>
                  <span className="text-foreground-500">主演：</span>
                  {movie.actors.join(" / ")}
                </p>
                <p>
                  <span className="text-foreground-500">片长：</span>
                  {movie.duration}
                </p>
              </div>

              <div className="mt-6 flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => setPlayOpen(true)}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm md:text-base font-label whitespace-nowrap transition-colors cursor-pointer"
                >
                  <i className="ri-play-fill text-xl"></i>
                  立即播放
                </button>
                <Link
                  to={`/category/${movie.type}`}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-100 hover:bg-background-200 border border-background-300/70 text-foreground-900 text-sm md:text-base font-label whitespace-nowrap transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-up-line text-xl"></i>
                  进入{movie.type === "movie" ? "电影" : "电视剧"}频道
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 剧情简介 */}
      <section className="mx-auto w-full max-w-[1440px] px-4 md:px-8 lg:px-12 pt-12 md:pt-16">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-3">
          <span className="w-1.5 h-7 rounded-full bg-accent-500"></span>
          剧情简介
        </h2>
        <p className="mt-4 text-sm md:text-base leading-8 text-foreground-600 max-w-4xl">
          {movie.description}
        </p>
      </section>

      {/* 播放演示区 */}
      <section className="mx-auto w-full max-w-[1440px] px-4 md:px-8 lg:px-12 pt-12 md:pt-16">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-3">
          <span className="w-1.5 h-7 rounded-full bg-accent-500"></span>
          在线播放
        </h2>
        <div className="mt-4 rounded-lg overflow-hidden bg-background-100 border border-background-200/70">
          <button
            type="button"
            onClick={() => setPlayOpen(true)}
            className="group relative w-full aspect-video bg-background-50 flex flex-col items-center justify-center text-center px-6 cursor-pointer"
          >
            <img
              src={movie.backdrop}
              alt={`${movie.title} 播放演示画面`}
              title={`${movie.title} 播放演示`}
              className="absolute inset-0 w-full h-full object-cover object-top opacity-40 transition-opacity group-hover:opacity-55"
            />
            <span className="relative w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-500/95 text-foreground-950 flex items-center justify-center">
              <i className="ri-play-fill text-4xl"></i>
            </span>
            <p className="relative mt-5 font-heading text-base md:text-lg font-bold text-foreground-900">
              点击进入播放演示
            </p>
            <p className="relative mt-1.5 text-xs md:text-sm text-foreground-500 max-w-md">
              因暂无真实播放接口，播放功能为演示状态，点击后将展示模拟播放界面。
            </p>
          </button>
        </div>
      </section>

      {/* 相关推荐 */}
      {related.length > 0 && (
        <section className="mx-auto w-full max-w-[1440px] px-4 md:px-8 lg:px-12 pt-12 md:pt-16">
          <SectionHeading
            title="相关推荐"
            subtitle="根据类型与热度为你推荐的相似影视"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-x-4 gap-y-7 md:gap-x-5">
            {related.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        </section>
      )}

      <PlayModal open={playOpen} movie={movie} onClose={() => setPlayOpen(false)} />
    </div>
  );
}